import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Plus, Trash2, Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";

export function AccessoryPicker({ onAdd, onClose, addedNames = [] }) {
  const [list, setList] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", price: "" });

  useEffect(() => {
    api.get("/accessories")
      .then((r) => setList(r.data.accessories || []))
      .catch(() => { toast.error("Couldn't load accessories"); setList([]); });
  }, []);

  const add = async (acc) => {
    setBusyId(acc.id);
    try { await onAdd(acc); } finally { setBusyId(null); }
  };

  const createNew = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      const r = await api.post("/accessories", { ...form, price: Number(form.price || 0) });
      setList((l) => [...(l || []), r.data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm({ name: "", description: "", price: "" });
      setShowNew(false);
      toast.success("Added to your accessories catalogue");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add"); }
  };

  const remove = async (acc) => {
    if (!window.confirm(`Remove "${acc.name}" from your catalogue?`)) return;
    try {
      await api.delete(`/accessories/${acc.id}`);
      setList((l) => l.filter((a) => a.id !== acc.id));
    } catch { toast.error("Couldn't remove"); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-lg border border-border shadow-xl w-full max-w-lg p-6 space-y-4 animate-fade-up max-h-[90vh] overflow-y-auto" data-testid="accessory-picker-modal">
        <div>
          <div className="label-overline flex items-center gap-1.5"><Gift className="w-3.5 h-3.5" /> Genuine accessories</div>
          <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Add to this quote</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Accessories appear in their own "Make it yours" section on the customer's quote — optional extras, never mixed with repair work.
          </p>
        </div>

        {list === null ? (
          <div className="py-8 text-center text-muted-foreground text-sm"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : (
          <ul className="divide-y divide-border border border-border rounded-lg" data-testid="accessory-picker-list">
            {list.map((acc) => {
              const already = addedNames.includes(acc.name);
              return (
                <li key={acc.id} className="flex items-center gap-3 p-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{acc.name}</div>
                    {acc.description && <div className="text-xs text-muted-foreground truncate">{acc.description}</div>}
                  </div>
                  <span className="font-mono-plex text-sm shrink-0">£{Number(acc.price || 0).toFixed(2)}</span>
                  <button
                    type="button"
                    onClick={() => add(acc)}
                    disabled={busyId === acc.id || already}
                    className={`shrink-0 text-xs font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-md border transition-colors ${already ? "border-success/40 text-success" : "border-primary text-primary hover:bg-primary/10"} disabled:opacity-60`}
                    data-testid={`accessory-add-${acc.id}`}
                  >
                    {already ? <><Check className="w-3 h-3 inline" /> Added</> : busyId === acc.id ? "Adding…" : "Add"}
                  </button>
                  <button type="button" onClick={() => remove(acc)} className="shrink-0 text-muted-foreground hover:text-destructive p-1" title="Remove from catalogue" data-testid={`accessory-remove-${acc.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              );
            })}
            {list.length === 0 && <li className="p-4 text-sm text-muted-foreground italic">No accessories in your catalogue yet.</li>}
          </ul>
        )}

        {showNew ? (
          <div className="space-y-2 border border-border rounded-lg p-3" data-testid="accessory-new-form">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Accessory name *" className="input-default" data-testid="accessory-new-name" />
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="One-line customer description" className="input-default" data-testid="accessory-new-description" />
            <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="Fitted price £" className="input-default" data-testid="accessory-new-price" />
            <div className="flex gap-2">
              <button type="button" onClick={createNew} className="btn-primary" data-testid="accessory-new-save">Save to catalogue</button>
              <button type="button" onClick={() => setShowNew(false)} className="btn-outline">Cancel</button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => setShowNew(true)} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="accessory-new-toggle">
            <Plus className="w-3.5 h-3.5" /> Add a new accessory to your catalogue
          </button>
        )}

        <div className="flex justify-end">
          <button type="button" onClick={onClose} className="btn-outline" data-testid="accessory-picker-done">Done</button>
        </div>
      </div>
    </div>
  );
}
